'use client';

import { Button, Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React, { useState } from 'react';

export default function BDDialog({
  openButton,
  children,
  open,
  onClose,
  panelClassName,
}: {
  openButton: React.ReactNode | null;
  children: React.ReactNode;
  open: boolean;
  onClose: () => void;
  panelClassName?: string;
}) {
  return (
    <div>
      {openButton}
      <Dialog open={open} onClose={onClose} className="relative z-30">
        <DialogBackdrop
          transition
          className="bg-bd-contrast/50 backdrop-blur-xs data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in fixed inset-0 transition-opacity"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className={clsx(
                'data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:data-closed:translate-y-0 sm:data-closed:scale-95 relative flex transform flex-col overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6',
                panelClassName
              )}
            >
              <Button
                type="button"
                key="close"
                onClick={onClose}
                className="text-bd-primary hover:text-bd-primary/80 ml-auto flex cursor-pointer justify-end hover:font-normal"
              >
                <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                <span className="sr-only">Close</span>
              </Button>
              {children}
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export function BDStyledDialog({
  openButton,
  children,
  open,
  onClose,
  imageOrIcon,
  title,
  description,
  panelClassName,
}: {
  openButton: React.ReactNode | null;
  children: React.ReactNode;
  open: boolean;
  onClose: () => void;
  imageOrIcon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  panelClassName?: string;
}) {
  return (
    <BDDialog openButton={openButton} open={open} onClose={onClose} panelClassName={panelClassName}>
      <div className="flex flex-1 flex-col items-center">
        {imageOrIcon && <div className="mx-auto flex items-center">{imageOrIcon}</div>}
        <div className="mt-4 text-center sm:mt-8">
          <DialogTitle
            as="h2"
            className="text-md text-bd-primary text-2xl font-semibold capitalize"
          >
            {title}
          </DialogTitle>
          {description && <div className="mt-4 text-sm font-light">{description}</div>}
        </div>
        <div className="mt-4 flex w-full flex-1 flex-col">{children}</div>
      </div>
    </BDDialog>
  );
}

interface OpenButtonProps {
  onClick: () => void;
}

interface FormProps {
  cancel?: () => void;
  afterSubmit?: () => void;
}

export function BDFormDialog({
  openButton,
  children,
  imageOrIcon,
  title,
  description,
  afterSubmit,
}: {
  openButton: React.ReactElement<OpenButtonProps>;
  children: React.ReactElement<FormProps>;
  imageOrIcon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  afterSubmit?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <BDStyledDialog
      openButton={React.cloneElement(openButton, { onClick: () => setOpen(true) })}
      open={open}
      onClose={() => setOpen(false)}
      imageOrIcon={imageOrIcon}
      title={title}
      description={description}
    >
      {React.isValidElement(children) &&
        React.cloneElement(children, {
          cancel: () => {
            setOpen(false);
          },
          afterSubmit: () => {
            setOpen(false);
            afterSubmit?.();
          },
        })}
    </BDStyledDialog>
  );
}
